<template>
  <div class="wf-canvas-wrap">
    <!-- Left rail: the DA library. Pick from the dropdown to click-add, or drag
         a chip onto the canvas. Both add a source node. -->
    <div v-if="!hideSourceRail" class="wf-canvas-rail">
      <div class="wf-rail-title">Data attributes</div>

      <!-- Dropdown picker: choosing an item adds it to the canvas. -->
      <select
        class="wf-rail-select"
        :disabled="loadingDas || !availableDas.length"
        @change="onPickerAdd($event)"
      >
        <option value="">{{ loadingDas ? 'Loading…' : '+ Add to canvas…' }}</option>
        <option v-for="da in availableDas" :key="'p' + da.id" :value="da.id" :disabled="!!runtimeParametersNote(da)">{{ da.name }}</option>
      </select>

      <div class="wf-rail-sub">or drag / click a source</div>

      <div
        v-for="da in availableDas"
        :key="da.id"
        class="wf-rail-card"
        :class="{ 'is-unavailable': runtimeParametersNote(da) }"
        :draggable="!runtimeParametersNote(da)"
        :title="runtimeParametersNote(da) || 'Drag onto the canvas, or click to add'"
        @dragstart="onRailDragStart($event, da)"
        @click="addDaByClick(da)"
      >
        <font-awesome-icon :icon="['fas', 'table']" class="wf-rail-card-icon" />
        <span class="wf-rail-card-name">{{ da.name }}</span>
      </div>
      <p v-if="!loadingDas && !availableDas.length" class="wf-rail-empty">
        No data attributes — create one first.
      </p>
    </div>

    <!-- Canvas -->
    <div
      class="wf-canvas"
      tabindex="0"
      @drop="onCanvasDrop"
      @dragover.prevent
      @keydown="onCanvasKeydown"
    >
      <VueFlow
        v-model:nodes="nodes"
        v-model:edges="edges"
        :default-viewport="{ zoom: 0.9 }"
        :min-zoom="0.2"
        :max-zoom="2"
        :elevate-nodes-on-select="true"
        :zoom-on-scroll="true"
        :pan-on-drag="true"
        :pan-on-scroll="false"
        :connection-radius="60"
        :snap-to-grid="false"
        :snap-grid="[GRID, GRID]"
        :multi-selection-key-code="['Meta', 'Shift']"
        :delete-key-code="null"
        :connection-mode="ConnectionMode.Loose"
        @connect="onConnect"
        @connect-start="connecting = true"
        @connect-end="connecting = false"
        @edge-click="onEdgeClick"
        @pane-click="onPaneClick"
        @node-click="onNodeClick"
        @pane-ready="onPaneReady"
      >
        <Background pattern-color="#e5e7eb" :gap="GRID" />
        <MiniMap
          pannable
          zoomable
          :node-color="miniMapNodeColor"
          class="wf-minimap"
        />

        <!-- Source node = a DA card, color-coded by role (Kairon brand).
             NO MODE SWITCH. A card is always draggable to reposition, and the
             connect handles sit on its left/right edges, appearing on hover
             (Figma/Tableau behaviour). Dragging a card body moves it; dragging
             a handle draws a join. Because the two gestures start in different
             places they never compete, which is what the old drawMode toggle
             existed to arbitrate — and why joining used to cost a click per
             join and silently switched itself back off afterwards. -->
        <template #node-source="{ data, selected }">
          <div
            class="wf-node"
            :class="[
              `wf-node--${data.role}`,
              { 'wf-node--selected': selected, 'wf-node--connecting': connecting },
            ]"
            :data-node-id="data.id"
            @dblclick.stop="openNodeEdges(data.id)"
          >
            <!-- Loose connection mode means either handle can start OR end a
                 connection, so a join can be drawn in whichever direction feels
                 natural and vue-flow sorts out the rest. -->
            <Handle
              type="target"
              :position="Position.Left"
              class="wf-handle wf-handle--in"
              :connectable="true"
            />
            <!-- DROP-ANYWHERE TARGET. A 12px dot is a punishing drop target, so
                 while (and only while) a connection is being dragged, this
                 full-card target handle lets the drop land anywhere on the
                 card. It is pointer-inert otherwise, so it never intercepts a
                 click or a card drag. This is gated on the transient
                 `connecting` state, NOT on a mode the user has to toggle. -->
            <Handle
              id="card-drop"
              type="target"
              :position="Position.Left"
              class="wf-handle-dropzone"
              :connectable="true"
            />
            <div class="wf-node-head">
              <font-awesome-icon :icon="['fas', 'table']" class="text-[10px]" />
              <span class="text-[9px] font-semibold uppercase tracking-wide">
                {{ roleLabel(data.role) }}
              </span>
            </div>
            <div class="wf-node-name">{{ data.name }}</div>
            <ul v-if="cardColumns(data).length" class="wf-node-cols">
              <li
                v-for="c in cardColumns(data)"
                :key="c"
                class="nodrag wf-node-col"
                draggable="true"
                title="Drag onto a sheet shelf"
                @dragstart.stop="dragColumn($event, data.workflow_id, c)"
              >{{ c }}</li>
            </ul>
            <button
class="wf-node-x" title="Remove (or select and press Delete)"
                    @click.stop="removeNode(data.id)">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
            <Handle
              type="source"
              :position="Position.Right"
              class="wf-handle wf-handle--out"
              :connectable="true"
            />
            <!-- Affordance: until a card has any connection, a pulsing hint on
                 the out-handle says "you can drag from here". It disappears the
                 moment the card is joined, so it teaches once instead of
                 decorating forever. -->
            <span v-if="!isConnected(data.id) && nodes.length > 1" class="wf-handle-pulse" />
          </div>
        </template>
      </VueFlow>

      <div v-if="!nodes.length" class="wf-canvas-hint">
        <div class="wf-guide">
          <p class="wf-guide-title">Build a workflow in three steps</p>
          <ol class="wf-guide-steps">
            <li>
              <span class="wf-guide-num">1</span>
              <span class="wf-guide-icon"><font-awesome-icon :icon="['fas', 'table']" /></span>
              <span><strong>Add a source</strong>Drag a data attribute from the Data panel onto the canvas.</span>
            </li>
            <li>
              <span class="wf-guide-num">2</span>
              <span class="wf-guide-icon"><font-awesome-icon :icon="['fas', 'diagram-project']" /></span>
              <span><strong>Join</strong>Drag from one card's edge to another and pick the shared column.</span>
            </li>
            <li>
              <span class="wf-guide-num">3</span>
              <span class="wf-guide-icon"><font-awesome-icon :icon="['fas', 'chart-simple']" /></span>
              <span><strong>Shape and explore</strong>Add calculations and filters, then chart it on a sheet below.</span>
            </li>
          </ol>
          <p class="wf-guide-foot">Or open the agent and describe what you want to build.</p>
        </div>
      </div>

      <!-- Unjoined nodes contribute nothing to studio_config; say so explicitly
           rather than dropping them from the compiled output in silence. -->
      <div v-if="unjoinedNames.length" class="wf-warn">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        <span>
          Not joined, so excluded from the workflow:
          <strong>{{ unjoinedNames.join(', ') }}</strong>. Drag from a card's edge to connect them.
        </span>
      </div>

      <!-- Auto-arrange (bottom-left), replacing the old join-mode toggle.
           Joining needs no tool now, so the slot goes to the thing a canvas
           actually needs: untangling itself. -->
      <button
        class="wf-arrange-btn wf-tip"
        data-tip="Tidy up (⇧A)"
        :disabled="!nodes.length"
        @click="autoArrange"
      >
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
      </button>
      <button
        v-if="!externalPreview"
        class="wf-preview-btn wf-tip"
        data-tip="Preview sample rows"
        :disabled="!nodes.length || previewLoading"
        @click="runPreview"
      >
        <font-awesome-icon :icon="['fas', 'table']" />
        {{ previewLoading ? 'Running…' : 'Preview' }}
      </button>

      <!-- Preview results: sample rows of the composed (joined) output. -->
      <div v-if="preview && !externalPreview" class="wf-preview">
        <div class="wf-preview-head">
          <span>Preview</span>
          <span v-if="preview.rows" class="wf-preview-count">{{ preview.rows.length }} sample rows</span>
          <button class="wf-preview-x" @click="preview = null"><font-awesome-icon :icon="['fas', 'times']" /></button>
        </div>
        <div v-if="preview.error" class="wf-preview-error">{{ preview.error }}</div>
        <div v-else class="wf-preview-scroll">
          <table class="wf-preview-table">
            <thead>
              <tr><th v-for="col in preview.columns" :key="col">{{ col }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in preview.rows" :key="i">
                <td v-for="col in preview.columns" :key="col">{{ row[col] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>


      <!-- Output columns panel: include/exclude + rename the composed output's
           columns. Absent overrides = SELECT * (all columns, no rename). -->
      <div v-if="showColumns" class="wf-cols">
        <div class="wf-cols-head">
          <font-awesome-icon :icon="['fas', 'list']" class="wf-cols-head-icon" />
          <span>Output columns</span>
          <span class="wf-cols-meta">{{ includedCount }}/{{ outputColumns.length }}</span>
          <button class="wf-cols-x" @click="showColumns = false"><font-awesome-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="wf-cols-body">
          <p v-if="!outputColumns.length" class="wf-cols-empty">Add data attributes to see their columns.</p>
          <div v-for="c in outputColumns" :key="c" class="wf-cols-row">
            <label class="wf-cols-check">
              <input type="checkbox" :checked="isIncluded(c)" @change="toggleColumn(c)" />
              <span class="wf-cols-name" :class="{ 'wf-cols-name--off': !isIncluded(c) }">{{ c }}</span>
            </label>
            <input
              class="wf-cols-rename"
              :placeholder="c"
              :disabled="!isIncluded(c)"
              :value="aliasFor(c)"
              @input="setAlias(c, $event.target.value)"
            />
          </div>
        </div>
      </div>


      <!-- Output filters panel: WHERE predicates on the composed result. -->
      <div v-if="showFilters" class="wf-filt">
        <div class="wf-filt-head">
          <font-awesome-icon :icon="['far', 'filter']" class="wf-filt-head-icon" />
          <span>Output filters</span>
          <button class="wf-filt-x" @click="showFilters = false"><font-awesome-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="wf-filt-body">
          <p v-if="!filters.length" class="wf-filt-empty">No filters — all rows are included.</p>
          <div v-for="(f, i) in filters" :key="i" class="wf-filt-row">
            <select v-model="f.column" class="wf-filt-col">
              <option v-for="c in filterColumns" :key="c" :value="c">{{ c }}</option>
            </select>
            <select v-model="f.op" class="wf-filt-op">
              <option v-for="op in filterOps" :key="op" :value="op">{{ op }}</option>
            </select>
            <input
              v-if="!_isNullOp(f.op)"
              v-model="f.value"
              class="wf-filt-val"
              :placeholder="_isListOp(f.op) ? 'a, b, c' : 'value'"
            />
            <button class="wf-filt-del" title="Remove" @click="removeFilter(i)">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          <button class="wf-filt-add" @click="addFilter">
            <font-awesome-icon :icon="['fas', 'plus']" /> Add filter
          </button>
        </div>
      </div>


      <div v-if="showCalc" class="wf-filt wf-calc">
        <div class="wf-filt-head">
          <font-awesome-icon :icon="calcIcon" class="wf-filt-head-icon" />
          <span>Calculated columns</span>
          <button class="wf-filt-x" @click="showCalc = false; calcEditing = null"><font-awesome-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="wf-filt-body">
          <WorkflowCalcEditor
            v-if="calcEditing !== null"
            :key="calcEditing"
            :model-value="calcEditing === 'new' ? null : calculated[calcEditing]"
            :preset="calcEditing === 'new' ? calcPreset : null"
            :columns="calcColumns"
            :taken-names="calculated.filter((_, i) => i !== calcEditing).map(c => c.name)"
            @save="saveCalc"
            @cancel="calcEditing = null"
          />
          <template v-else>
            <p v-if="!calculated.length" class="wf-filt-empty">No calculated columns yet.</p>
            <div v-for="(c, i) in calculated" :key="c.name" class="wf-calc-row">
              <button class="wf-calc-name" @click="calcEditing = i">{{ c.name }}</button>
              <span class="wf-calc-fn">{{ c.fn }}</span>
              <button class="wf-filt-del" title="Remove" @click="removeCalc(i)">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
            <button class="wf-filt-add" @click="calcEditing = 'new'">
              <font-awesome-icon :icon="['fas', 'plus']" /> Add calculated column
            </button>
          </template>
        </div>
      </div>


      <!-- Live studio_config panel: recompiled from the graph on every change,
           so you can see exactly what will materialize as you build. -->
      <div v-if="showConfig" class="wf-cfg">
        <div class="wf-cfg-head">
          <font-awesome-icon :icon="['fas', 'file-lines']" class="wf-cfg-head-icon" />
          <span>studio_config</span>
          <span class="wf-cfg-meta">{{ liveConfig.steps.length }} step{{ liveConfig.steps.length === 1 ? '' : 's' }}</span>
          <button class="wf-cfg-x" @click="showConfig = false"><font-awesome-icon :icon="['fas', 'times']" /></button>
        </div>
        <pre class="wf-cfg-body">{{ prettyConfig }}</pre>
      </div>

      <div class="wf-tools" role="toolbar" aria-label="Canvas tools">
        <button
          class="wf-calc-btn wf-tip"
          :class="{ 'wf-calc-btn--on': showCalc }"
          data-tip="Calculated columns"
          :disabled="!outputColumns.length"
          @click="toggleCalc"
        >
          <font-awesome-icon :icon="calcIcon" />
          <span v-if="calculated.length" class="wf-filt-badge">{{ calculated.length }}</span>
        </button>
        <button
          class="wf-filt-btn wf-tip"
          :class="{ 'wf-filt-btn--on': showFilters }"
          data-tip="Output filters"
          :disabled="!outputColumns.length"
          @click="showFilters = !showFilters"
        >
          <font-awesome-icon :icon="['far', 'filter']" />
          <span v-if="filters.length" class="wf-filt-badge">{{ filters.length }}</span>
        </button>
        <button
          class="wf-cols-btn wf-tip"
          :class="{ 'wf-cols-btn--on': showColumns }"
          data-tip="Output columns"
          :disabled="!outputColumns.length"
          @click="showColumns = !showColumns"
        >
          <font-awesome-icon :icon="['fas', 'list']" />
        </button>
        <button
          class="wf-cfg-btn wf-tip"
          :class="{ 'wf-cfg-btn--on': showConfig }"
          data-tip="Studio config"
          @click="showConfig = !showConfig"
        >
          <font-awesome-icon :icon="['fas', 'file-lines']" />
        </button>
        <span class="wf-tools-sep" aria-hidden="true"></span>
        <button class="wf-zoom-btn wf-tip" data-tip="Zoom in" @click="zoomIn">
          <span class="wf-zoom-glyph">+</span>
        </button>
        <button class="wf-zoom-btn wf-tip" data-tip="Zoom out" @click="zoomOut">
          <span class="wf-zoom-glyph">−</span>
        </button>
        <button class="wf-zoom-btn wf-tip" data-tip="Fit to view" @click="fitView">
          <font-awesome-icon :icon="['fas', 'up-right-and-down-left-from-center']" />
        </button>
      </div>
    </div>

    <!-- Edge editor: a popover anchored AT the clicked edge rather than a
         panel pinned to the corner. Editing a join is a small decision about
         one connection, so it should happen where that connection is — the
         corner panel made you look away from the thing you were editing, and
         gave no clue which edge it belonged to. Closes on Escape or a click
         on empty canvas. -->
    <Teleport to="body">
      <div
        v-if="editingEdge"
        class="wf-join-editor"
        :style="edgePopoverStyle"
        @click.stop
      >
        <div class="wf-je-title">
          <font-awesome-icon :icon="['fas', isUnionEdge ? 'layer-group' : 'link']" class="wf-je-title-icon" />
          {{ isUnionEdge ? 'Configure union' : 'Configure join' }}
        </div>

        <!-- Relationship: join vs union. Union appends rows (no key); join matches. -->
        <label class="wf-je-label">Relationship</label>
        <select v-model="editingEdge.data.step_op" class="wf-je-input">
          <option value="join">Join (match on a column)</option>
          <option value="union">Union (append rows, de-duplicated)</option>
          <option value="union_all">Union all (append rows, keep duplicates)</option>
        </select>

        <!-- Join-only fields: type + on-column. Hidden for unions. -->
        <template v-if="!isUnionEdge">
          <label class="wf-je-label">Join type</label>
          <select v-model="editingEdge.data.join_type" class="wf-je-input">
            <option value="JOIN">Inner join</option>
            <option value="LEFT JOIN">Left join</option>
            <option value="RIGHT JOIN">Right join</option>
            <option value="FULL OUTER JOIN">Full outer join</option>
            <option value="CROSS JOIN">Cross join (no key)</option>
          </select>

          <template v-if="editingEdge.data.join_type !== 'CROSS JOIN'">
            <label class="wf-je-label">On column</label>
            <p v-if="editingEdge.data.join_keys && editingEdge.data.join_keys.length > 1" class="wf-je-note">
              Joined on {{ editingEdge.data.join_keys.join(' + ') }}. Choosing one column replaces the compound key.
            </p>
            <select
              v-if="editSharedColumns.length"
              v-model="editingEdge.data.join_key"
              class="wf-je-input wf-je-mono"
              @change="editingEdge.data.join_keys = null"
            >
              <option v-for="c in editSharedColumns" :key="c" :value="c">{{ c }}</option>
            </select>
            <input
              v-else
              v-model.trim="editingEdge.data.join_key"
              class="wf-je-input wf-je-mono"
              placeholder="BENE_MBI_ID"
              @input="editingEdge.data.join_keys = null"
            />
            <p v-if="!editSharedColumns.length" class="wf-je-note">
              No shared column detected — enter one manually.
            </p>
          </template>
        </template>
        <p v-else class="wf-je-note">
          Union appends this source's rows to the result. Both sources should return
          the same columns; a mismatch is flagged when you preview or run.
        </p>

        <div class="wf-je-actions">
          <button class="wf-je-btn wf-je-btn--remove" @click="deleteEdge(editingEdge.id)">Remove</button>
          <button class="wf-je-btn wf-je-btn--done" @click="editingEdge = null">Done</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { toRaw } from 'vue';
import { VueFlow, Handle, Position, ConnectionMode } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { MiniMap } from '@vue-flow/minimap';
import { publishCanvas, clearCanvas, canvasContext } from '@/services/canvasContext';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';
import WorkflowCalcEditor from './WorkflowCalcEditor.vue';
import { runtimeParametersNote } from '@/utils/daRuntimeParams';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/minimap/dist/style.css';

const ATTR_BASE = '/api/impact_analytics/attributes/';
const PREVIEW_BASE = '/api/etl_module/data-workflows/';

export default {
  name: 'WorkflowJoinCanvas',

  components: { VueFlow, Background, MiniMap, Handle, WorkflowCalcEditor },

  // v-model:studioConfig — the canvas is a visual editor over the same
  // studio_config the backend compiles. Emits it whenever the graph changes.
  props: {
    modelValue: { type: Object, default: null }, // existing studio_config (edit)
    // Which workflow is being edited, for the agent's context only. Optional:
    // the canvas composes fine without it (a brand-new workflow has no row
    // yet), it just lets the agent say "the Readmission cohort workflow"
    // instead of "the canvas".
    workflowMeta: { type: Object, default: null }, // { id, name, status }
    externalPreview: { type: Boolean, default: false },
    hideSourceRail: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'select', 'refused'],

  data() {
    return {
      Position, // exposed so the template can reference Position.Left/Right
      // Loose mode lets either handle start or finish a connection, so a join
      // can be drawn in whichever direction the user reaches for first.
      ConnectionMode,
      availableDas: [],
      loadingDas: false,
      nodes: [],
      edges: [],
      editingEdge: null,
      seq: 0,
      // Canvas geometry. GRID drives both the Background dots and snap-to-grid
      // so cards align to the same lattice they appear to sit on.
      GRID: 18,
      NODE_W: 190,
      NODE_H: 150,
      EDGE_GAP: 160,
      edgePopoverStyle: null,   // popover anchored at the clicked edge
      // True only while a connection is being dragged. Drives the full-card
      // drop zone: a 12px dot is a punishing drop target, so during a drag the
      // whole card becomes droppable. Bound as a class on our own node element
      // rather than via a :deep() descendant selector off vue-flow's pane —
      // scoped styles do not reliably match across that boundary.
      connecting: false,
      vf: null,          // vue-flow instance (from @pane-ready) for zoom controls
      preview: null,     // {columns, rows} | {error} sample-row preview result
      previewLoading: false,
      showConfig: false, // studio_config panel open? (live compiled view)
      showColumns: false, // output-columns picker panel open?
      // colName -> { included: bool, alias: string }. Absent = included, no rename.
      columnOverrides: {},
      showFilters: false, // output-filters panel open?
      // [{ column, op, value }] — WHERE predicates on the composed output.
      filters: [],
      calculated: [],
      showCalc: false,
      calcEditing: null,
      calcPreset: null,
      calcIcon: faCalculator,
      filterOps: ['=', '!=', '>', '>=', '<', '<=', 'LIKE', 'NOT LIKE', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'],
      // True while rebuilding the graph from an incoming studio_config, so the
      // nodes/edges watchers don't echo the change straight back as an emit
      // (which would fight the parent and re-trigger the modelValue watcher).
      hydrating: false,
      // The exact object identity we last emitted. The parent's v-model bounces
      // it straight back as modelValue; identity is the ONLY reliable echo test.
      // Comparing serialized VALUES instead (the old approach) conflated two
      // different things: an authoritative reload from the parent whose config
      // happens to round-trip equal was skipped, while a key-order difference
      // from a Postgres JSONField round-trip forced a spurious rehydrate that
      // reset every node position mid-edit.
      lastEmitted: null,
      lastEmittedKey: '',
    };
  },

  computed: {
    // Is the edge being edited a union (append rows) rather than a join?
    isUnionEdge() {
      const op = this.editingEdge && this.editingEdge.data && this.editingEdge.data.step_op;
      return op === 'union' || op === 'union_all';
    },

    // How many output columns are currently included (for the panel counter).
    includedCount() {
      return this.outputColumns.filter(c => this.isIncluded(c)).length;
    },

    // The composed output's candidate columns — the de-duplicated union of every
    // node's columns. Feeds both the column picker and the filter column dropdown.
    calcColumns() {
      return this.outputColumns
        .filter((c) => this.isIncluded(c))
        .map((c) => (this.aliasFor(c) || c));
    },

    filterColumns() {
      return [...this.outputColumns, ...this.calculated.map((c) => c.name)];
    },

    outputColumns() {
      const seen = new Set();
      const out = [];
      for (const n of this.nodes) {
        for (const c of (n.data.columns || [])) {
          if (!seen.has(c)) { seen.add(c); out.push(c); }
        }
      }
      return out;
    },

    // Columns common to the two nodes an edge connects — offered as join-key
    // options in the editor (Tableau-style "join on a shared field").
    editSharedColumns() {
      if (!this.editingEdge) return [];
      const src = this.nodes.find(n => n.id === this.editingEdge.source);
      const tgt = this.nodes.find(n => n.id === this.editingEdge.target);
      const a = (src && src.data.columns) || [];
      const b = new Set((tgt && tgt.data.columns) || []);
      return a.filter(c => b.has(c));
    },

    // The live compiled studio_config (same builder the parent gets), shown in
    // the studio-config panel. Touching nodes/edges registers them as reactive
    // deps so this recomputes whenever the graph changes.
    liveConfig() {
      void this.nodes; void this.edges;
      return this.buildStudioConfig();
    },
    prettyConfig() {
      return JSON.stringify(this.liveConfig, null, 2);
    },

    // Names of canvas nodes excluded from studio_config because nothing joins
    // them. Drives the warning banner so the omission is never silent.
    unjoinedNames() {
      void this.nodes; void this.edges;
      return this.unjoinedNodes().map(n => n.data.name);
    },
  },

  watch: {
    // Any graph change → recompute studio_config and emit upward. Skipped while
    // hydrating (rebuilding from an incoming config) so we don't echo it back.
    nodes: {
      handler() {
        if (!this.hydrating) this.emitConfig();
        this.publishToAgent();
      },
      deep: true,
    },
    edges: {
      handler() {
        this.edges.forEach((e) => {
          const label = this.edgeLabel(e.data);
          if (e.label !== label) e.label = label;
          if (!e.labelBgPadding) {
            e.labelShowBg = true;
            e.labelBgPadding = [8, 4];
            e.labelBgBorderRadius = 4;
          }
        });
        if (!this.hydrating) this.emitConfig();
        this.publishToAgent();
      },
      deep: true,
    },
    filters: { handler() { if (!this.hydrating) this.emitConfig(); }, deep: true },
    calculated: { handler() { if (!this.hydrating) this.emitConfig(); this.publishToAgent(); }, deep: true },

    // Parent set a new studio_config (opened a saved workflow, or reset for a
    // new one) → rebuild the graph. Ignore the echo of our own last emit.
    modelValue: {
      handler(cfg) {
        if (this.isOwnEcho(cfg)) return;
        this.rehydrate(cfg);
      },
      deep: true,
    },
  },

  async mounted() {
    await this.fetchDas();
    // DAs are needed to resolve each node's columns, so rehydrate any config the
    // parent already passed in AFTER the library loads (edit-open case).
    if (this.modelValue && this.modelValue.steps && this.modelValue.steps.length) {
      this.rehydrate(this.modelValue);
    }
    this.publishToAgent();
  },

  // Stop describing a canvas the user has navigated away from — otherwise the
  // agent keeps answering about a graph that is no longer on screen.
  beforeUnmount() {
    clearCanvas();
  },

  methods: {
    runtimeParametersNote,
    cardColumns(data) {
      if (data.columns && data.columns.length) return data.columns;
      return Object.keys((canvasContext.columnTypes || {})[data.workflow_id] || {});
    },
    dragColumn(ev, workflowId, column) {
      const type = ((canvasContext.columnTypes || {})[workflowId] || {})[column] || 'text';
      ev.dataTransfer.setData('field', JSON.stringify({ name: column, type }));
      ev.dataTransfer.effectAllowed = 'copy';
    },
    roleLabel(role) {
      return { population: 'Base', denominator: 'Joined', intervention: 'Intervention' }[role] || role;
    },

    async fetchDas() {
      this.loadingDas = true;
      try {
        const { data } = await axios.get(ATTR_BASE);
        this.availableDas = Array.isArray(data) ? data : (data.results || []);
      } catch {
        this.availableDas = [];
      } finally {
        this.loadingDas = false;
      }
    },

    onRailDragStart(ev, da) {
      ev.dataTransfer.setData('da', JSON.stringify({ id: da.id, name: da.name }));
      ev.dataTransfer.effectAllowed = 'move';
    },

    // Shared placement: first node is the base (population), the rest are joins.
    // pos is canvas-relative; auto-lays out click-added nodes in a row. Columns
    // come from the full DA in availableDas (drag passes only id/name).
    addNode(da, pos) {
      const nodeId = `n${this.seq++}`;
      // Base is whether a 'population' node already exists, NOT whether the
      // canvas is empty — otherwise removing the base and adding a node leaves
      // the graph with no base at all (and no way to get one back).
      const isBase = !this.nodes.some(n => n.data.role === 'population');
      const at = pos || this.nextFreeSlot();
      const full = this.availableDas.find(d => d.id === da.id) || da;
      const refusal = runtimeParametersNote(full);
      if (refusal) {
        this.$emit('refused', refusal);
        return;
      }
      this.nodes.push({
        id: nodeId,
        type: 'source',
        position: at,
        data: {
          id: nodeId, workflow_id: full.workflow_id, name: da.name,
          role: isBase ? 'population' : 'denominator',
          columns: full.output_columns || [],
        },
      });
    },

    // Click a DA (dropdown or chip) to add it to the canvas (no drag needed).
    addDaByClick(da) {
      if (da && da.id) this.addNode({ id: da.id, name: da.name });
    },

    // Dropdown picker: add the chosen DA, then reset the select to its prompt.
    onPickerAdd(ev) {
      const id = Number(ev.target.value);
      const da = this.availableDas.find(d => d.id === id);
      if (da) this.addNode({ id: da.id, name: da.name });
      ev.target.value = '';
    },

    onCanvasDrop(ev) {
      ev.preventDefault();
      const raw = ev.dataTransfer.getData('da');
      if (!raw) return;
      const da = JSON.parse(raw);
      const rect = ev.currentTarget.getBoundingClientRect();
      this.addNode(da, { x: ev.clientX - rect.left - 70, y: ev.clientY - rect.top - 30 });
    },

    // --- layout ---------------------------------------------------------
    // A free spot for a new card: walk the grid left-to-right, top-to-bottom
    // and take the first cell that does not overlap an existing card. The old
    // behaviour (x = 60 + count * 200) marched off-screen after ~5 cards and
    // collided immediately with anything dropped by hand.
    edgeLabel(data) {
      const d = data || {};
      if (d.step_op === 'union' || d.step_op === 'union_all') return d.step_op === 'union_all' ? 'UNION ALL' : 'UNION';
      if (d.join_type === 'CROSS JOIN') return 'CROSS';
      const keys = (d.join_keys && d.join_keys.length > 1) ? d.join_keys.join(' + ') : (d.join_key || '');
      const kind = d.join_type && d.join_type !== 'JOIN' ? `${d.join_type.replace(' JOIN', '').replace(' OUTER', '')} · ` : '';
      return keys ? `${kind}${keys}` : `${kind}pick a key`;
    },

    edgeGap(edges = this.edges) {
      const longest = Math.max(0, ...edges.map((e) => this.edgeLabel(e.data).length));
      return Math.max(this.EDGE_GAP, Math.round(longest * 7.2) + 72);
    },

    nextFreeSlot() {
      const COL = this.NODE_W + this.edgeGap(), ROW = this.NODE_H + 40;
      const taken = this.nodes.map(n => n.position);
      const clashes = (x, y) => taken.some(
        p => Math.abs(p.x - x) < COL * 0.9 && Math.abs(p.y - y) < ROW * 0.9,
      );
      for (let row = 0; row < 12; row += 1) {
        for (let col = 0; col < 8; col += 1) {
          const x = 60 + col * COL, y = 60 + row * ROW;
          if (!clashes(x, y)) return { x, y };
        }
      }
      return { x: 60, y: 60 };
    },

    // Tidy up: lay the graph out left-to-right in dependency order, so a chain
    // of joins reads as a chain. Nodes are ranked by how far they sit from the
    // base (breadth-first over edges); unconnected cards park in a final
    // column rather than being scattered through the graph.
    autoArrange() {
      if (!this.nodes.length) return;
      const COL = this.NODE_W + this.edgeGap(), ROW = this.NODE_H + 30;
      const rank = new Map();
      const base = this.nodes.find(n => n.data.role === 'population') || this.nodes[0];
      rank.set(base.id, 0);

      // BFS outward from the base along edges in either direction.
      let frontier = [base.id], depth = 0, guard = 0;
      while (frontier.length && guard++ < 50) {
        depth += 1;
        const next = [];
        for (const id of frontier) {
          for (const e of this.edges) {
            const other = e.source === id ? e.target : (e.target === id ? e.source : null);
            if (other && !rank.has(other)) { rank.set(other, depth); next.push(other); }
          }
        }
        frontier = next;
      }

      const unranked = this.nodes.filter(n => !rank.has(n.id));
      const maxRank = Math.max(0, ...rank.values());
      unranked.forEach(n => rank.set(n.id, maxRank + 1));

      const byRank = {};
      for (const n of this.nodes) {
        const r = rank.get(n.id);
        (byRank[r] = byRank[r] || []).push(n);
      }
      const snap = v => Math.round(v / this.GRID) * this.GRID;
      Object.keys(byRank).forEach(r => {
        byRank[r].forEach((n, i) => {
          n.position = { x: snap(60 + Number(r) * COL), y: snap(60 + i * ROW) };
        });
      });
      this.nodes = [...this.nodes];
      this.$nextTick(() => this.fitView());
    },

    // Keep dragged cards on the same lattice the grid background implies.
    // --- keyboard --------------------------------------------------------
    // A canvas you can only operate with a mouse is not free-flowing. Delete
    // removes the selection, Escape dismisses whatever is open, ⇧A tidies up,
    // and ⌘/Ctrl+A selects everything.
    onCanvasKeydown(ev) {
      const tag = (ev.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;

      if (ev.key === 'Escape') {
        this.editingEdge = null;
        this.preview = null;
        return;
      }
      if (ev.key === 'Delete' || ev.key === 'Backspace') {
        const selectedNodes = this.nodes.filter(n => n.selected);
        const selectedEdges = this.edges.filter(e => e.selected);
        if (!selectedNodes.length && !selectedEdges.length) return;
        ev.preventDefault();
        selectedEdges.forEach(e => this.deleteEdge(e.id));
        selectedNodes.forEach(n => this.removeNode(n.data.id));
        return;
      }
      if (ev.key.toLowerCase() === 'a' && ev.shiftKey && !ev.metaKey && !ev.ctrlKey) {
        ev.preventDefault();
        this.autoArrange();
        return;
      }
      if (ev.key.toLowerCase() === 'a' && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        this.nodes = this.nodes.map(n => ({ ...n, selected: true }));
      }
    },

    // Publish the graph's SHAPE to the agent: names, roles, columns, joins.
    // Deliberately no row data — the agent fetches rows through the governed
    // preview endpoint when it needs them, so tenant scoping stays in one
    // place. Published even while hydrating, because opening a saved workflow
    // is exactly when the agent should know what is on screen.
    publishToAgent() {
      const nameOf = id => {
        const n = this.nodes.find(x => x.id === id);
        return n ? n.data.name : id;
      };
      publishCanvas({
        workflow: this.workflowMeta || null,
        nodes: this.nodes.map(n => ({
          id: n.id, workflow_id: n.data.workflow_id, name: n.data.name,
          role: n.data.role, columns: n.data.columns || [],
        })),
        edges: this.edges.map(e => ({
          source: e.source, target: e.target,
          sourceName: nameOf(e.source), targetName: nameOf(e.target),
          step_op: (e.data && e.data.step_op) || 'join',
          join_type: (e.data && e.data.join_type) || null,
          join_key: (e.data && (e.data.join_keys && e.data.join_keys.length > 1 ? e.data.join_keys.join(' + ') : e.data.join_key)) || null,
        })),
        unjoined: this.unjoinedNames,
        calculated: this.calculated.map((c) => ({ name: c.name, fn: c.fn })),
      });
    },

    // --- small helpers ---------------------------------------------------
    isConnected(nodeId) {
      return this.edges.some(e => e.source === nodeId || e.target === nodeId);
    },

    openNodeEdges(nodeId) {
      const edge = this.edges.find(e => e.source === nodeId || e.target === nodeId);
      if (edge) this.editingEdge = edge;
    },

    // Minimap dots inherit the card's role colour, so the map reads as a
    // scaled-down picture of the canvas rather than a grey blob.
    miniMapNodeColor(node) {
      return {
        population: '#f97316',
        denominator: '#3b82f6',
        intervention: '#22c55e',
      }[node.data && node.data.role] || '#9ca3af';
    },

    // vue-flow hands the instance to @pane-ready; keep it so our own zoom
    // buttons can drive zoomIn/zoomOut/fitView (replaces vue-flow's <Controls>,
    // whose tooltips get CSS-clipped by its nested containers).
    onPaneReady(vfInstance) {
      this.vf = vfInstance;
    },
    zoomIn() { this.vf && this.vf.zoomIn(); },
    zoomOut() { this.vf && this.vf.zoomOut(); },
    fitView() { this.vf && this.vf.fitView({ padding: 0.2 }); },

    // Preview sample rows of the composed (joined) output. POSTs the current
    // studio_config for the server to compile (build_sql_from_studio) + run
    // LIMIT n. NOTE: this preview-sample endpoint is a follow-up on the backend
    // (the existing test-sql takes raw SQL, not a studio_config); the harness
    // stubs the response so the UX is demoable now.
    async runPreview() {
      this.previewLoading = true;
      this.preview = null;
      try {
        const { data } = await axios.post(`${PREVIEW_BASE}preview-sample/`, {
          studio_config: this.buildStudioConfig(),
          limit: 20,
        });
        this.preview = data.error
          ? { error: data.error }
          : { columns: data.columns || [], rows: data.rows || [] };
      } catch (e) {
        this.preview = { error: e.response?.data?.error || 'Preview failed.' };
      } finally {
        this.previewLoading = false;
      }
    },

    // Joins are drawn with vue-flow's NATIVE connection system (@connect fires
    // {source, target}), so there's no hand-rolled pointer/rubber-band code —
    // that path fought vue-flow and forced node-dragging off. Nodes now stay
    // freely draggable at all times; in join mode a full-card source handle lets
    // you drag from anywhere on a card onto another to connect.
    onConnect({ source, target }) {
      // A drawn connection = a join between two source nodes. Default to an
      // inner join on BENE_MBI_ID (the common spine); editable by clicking it.
      if (!source || !target || source === target) return;
      const id = `e-${source}-${target}`;
      if (this.edges.some(e => e.id === id)) return;
      this.edges.push({
        id, source, target, updatable: true,
        label: 'on BENE_MBI_ID',
        data: { step_op: 'join', join_type: 'JOIN', join_key: 'BENE_MBI_ID' },
      });
    },

    onEdgeClick({ edge, event }) {
      this.editingEdge = this.edges.find(e => e.id === edge.id) || null;
      if (!this.editingEdge) return;
      // Anchor the popover where the edge was clicked, clamped inside the
      // viewport so it never opens half off-screen near an edge or corner.
      const host = this.$el.querySelector('.wf-canvas');
      const r = host ? host.getBoundingClientRect() : { right: window.innerWidth, top: 0 };
      const W = 260, H = 340, PAD = 12;
      const cx = event ? event.clientX + 12 : r.right - W - PAD;
      const cy = event ? event.clientY - 40 : r.top + PAD;
      const x = Math.min(Math.max(cx, PAD), window.innerWidth - W - PAD);
      const y = Math.min(Math.max(cy, PAD), Math.max(PAD, window.innerHeight - H - PAD));
      this.edgePopoverStyle = { left: `${x}px`, top: `${y}px` };
    },

    // Clicking empty canvas dismisses the popover — the usual "click away to
    // close" a popover implies but a corner panel never honoured.
    onPaneClick() {
      this.editingEdge = null;
      this.$emit('select', null);
    },

    onNodeClick({ node }) {
      this.$emit('select', { workflow_id: node.data.workflow_id, name: node.data.name });
    },

    deleteEdge(id) {
      this.edges = this.edges.filter(e => e.id !== id);
      this.editingEdge = null;
    },

    // Removing a node drops its joins too. If it was the BASE, the leftmost
    // survivor is promoted — without a 'population' node buildStudioConfig
    // compiles to an empty config, so the graph would silently stop saving.
    removeNode(id) {
      const removed = this.nodes.find(n => n.id === id);
      // Remember each dropped edge's join settings against its target, so a
      // base promotion can restore them rather than resetting to the default.
      this._priorJoins = this._priorJoins || {};
      for (const e of this.edges) {
        if (e.source === id || e.target === id) this._priorJoins[e.target] = { ...e.data };
      }
      this.nodes = this.nodes.filter(n => n.id !== id);
      this.edges = this.edges.filter(e => e.source !== id && e.target !== id);
      if (removed && removed.data.role === 'population') this.promoteBase();
    },

    // Make sure exactly one node carries role 'population' (the join base).
    // Picks the leftmost node so the promotion matches the visual layout.
    //
    // Removing the old base also removed every edge that touched it, which
    // orphans its former join targets. Those are re-linked to the new base
    // (carrying their original join type/key) so a star graph survives base
    // removal instead of quietly shedding its joins.
    promoteBase() {
      if (!this.nodes.length) return;
      const next = this.nodes.reduce((a, b) => (b.position.x < a.position.x ? b : a));
      for (const n of this.nodes) {
        n.data.role = n.id === next.id ? 'population' : 'denominator';
      }
      const orphanEdges = this.edges
        .filter(e => e.target !== next.id)
        .map(e => (e.source === next.id ? e : { ...e, id: `e-${next.id}-${e.target}`, source: next.id }));
      const joined = new Set(orphanEdges.map(e => e.target));
      for (const n of this.nodes) {
        if (n.id === next.id || joined.has(n.id)) continue;
        const prior = this._lastJoinFor(n.id);
        orphanEdges.push({
          id: `e-${next.id}-${n.id}`,
          source: next.id, target: n.id, updatable: true,
          label: prior.join_keys && prior.join_keys.length > 1 ? `on ${prior.join_keys.join(' + ')}` : (prior.join_key ? `on ${prior.join_key}` : 'join'),
          data: { ...prior },
        });
      }
      this.edges = orphanEdges;
    },

    // The join type/key a node last used, so re-parenting it to a new base keeps
    // the user's configured join instead of silently reverting to the default.
    _lastJoinFor(nodeId) {
      const prior = this._priorJoins && this._priorJoins[nodeId];
      return prior ? { ...prior } : { join_type: 'JOIN', join_key: 'BENE_MBI_ID' };
    },

    // Compile the visual graph into the SAME studio_config the backend expects:
    // base node = population step, each joined node = a denominator step with
    // join_on_sql derived from its incoming edge. Shared by emitConfig + preview.
    //
    // Iterates NODES, not edges: edge-driven iteration emitted a node twice when
    // it was the target of two edges, and silently dropped any node the user
    // hadn't joined yet. Unjoined nodes are reported by unjoinedNodes().
    // The compiler aliases steps POSITIONALLY (step 0 -> 'a', step 1 -> 'b', …)
    // and interpolates join_on_sql verbatim into the ON clause. So a join's
    // SOURCE is expressed by which alias sits on the left-hand side — there is
    // no separate source field in the schema. Emitting a hardcoded "a.KEY" made
    // every join read as "joined to the base", which is why a chain (A->B->C)
    // came back as a star. We emit the real source's alias instead.
    buildStudioConfig() {
      const base = this.nodes.find(n => n.data.role === 'population');
      if (!base) return { steps: [], primary_key_columns: [] };

      // Order matters: a node's source must occupy an EARLIER step than the
      // node itself, or its alias isn't in scope in the compiled SQL. Walk the
      // graph breadth-first from the base to guarantee that.
      const ordered = this._orderedJoinNodes(base);

      // node id -> its positional alias in the emitted steps array.
      const aliasOf = { [base.id]: 'a' };
      ordered.forEach((n, i) => { aliasOf[n.id] = String.fromCharCode(98 + i); });

      const steps = [{ workflow_id: base.data.workflow_id, role: 'population', join_on_sql: '' }];
      let pk = null;
      for (const n of ordered) {
        const e = this.edges.find(edge => edge.target === n.id);
        const op = e.data.step_op || 'join';
        if (op === 'union' || op === 'union_all') {
          // Union appends rows — no join key, no ON clause.
          steps.push({ workflow_id: n.data.workflow_id, role: 'denominator', step_op: op });
          continue;
        }
        const joinType = e.data.join_type || 'JOIN';
        const key = (e.data.join_key || '').trim();
        const keys = (e.data.join_keys && e.data.join_keys.length > 1) ? e.data.join_keys : (key ? [key] : []);
        if (keys.length && !pk) pk = keys;
        const src = aliasOf[e.source] || 'a';
        // CROSS JOIN takes no ON clause; every other join type does (when a key is set).
        const onSql = (joinType !== 'CROSS JOIN' && keys.length)
          ? keys.map((k) => `${src}.${k} = ${aliasOf[n.id]}.${k}`).join(' AND ') : '';
        steps.push({
          workflow_id: n.data.workflow_id,
          role: 'denominator',
          step_op: 'join',
          join_type: joinType,
          join_on_sql: onSql,
        });
      }
      const cfg = { steps, primary_key_columns: pk || [] };

      // Column selection / rename. Only emit output_columns when the user has
      // actually narrowed or renamed something — otherwise leave it absent so the
      // backend keeps its SELECT * (deduped) default. Each entry is a bare name,
      // or {col, as} for a rename.
      const oc = this._emitOutputColumns();
      if (oc) cfg.output_columns = oc;
      // Output filters (WHERE on the composed result). Only emit valid rows: a
      // column + op, and a value unless the op is a null-check. Absent = no WHERE.
      const of = this.filters
        .filter(f => f.column && f.op && (this._isNullOp(f.op) || this._isListOp(f.op) || f.value !== ''))
        .map(f => this._filterToConfig(f));
      if (of.length) cfg.output_filters = of;
      if (this.calculated.length) cfg.calculated_columns = JSON.parse(JSON.stringify(this.calculated));
      return cfg;
    },

    // --- calculated columns ---
    openCalcEditor(preset = null) {
      this.calcPreset = preset;
      this.showCalc = true;
      this.showFilters = false;
      this.showColumns = false;
      this.calcEditing = 'new';
    },
    editCalcByName(name) {
      const i = this.calculated.findIndex((c) => c.name === name);
      if (i < 0) return;
      this.showCalc = true;
      this.showFilters = false;
      this.showColumns = false;
      this.calcEditing = i;
    },
    openFilterFor(column) {
      this.filters = [...this.filters, { column, op: '=', value: '' }];
      this.showFilters = true;
      this.showCalc = false;
      this.showColumns = false;
    },
    toggleCalc() {
      this.showCalc = !this.showCalc;
      this.calcEditing = null;
      if (this.showCalc) { this.showFilters = false; this.showColumns = false; }
    },
    saveCalc(spec) {
      const next = [...this.calculated];
      if (this.calcEditing === 'new') next.push(spec);
      else next.splice(this.calcEditing, 1, spec);
      this.calculated = next;
      this.calcEditing = null;
    },
    removeCalc(i) {
      this.calculated = this.calculated.filter((_, idx) => idx !== i);
    },

    // --- output filters ---
    _isNullOp(op) { return op === 'IS NULL' || op === 'IS NOT NULL'; },
    _isListOp(op) { return op === 'IN' || op === 'NOT IN'; },

    addFilter() {
      const col = this.outputColumns[0] || '';
      this.filters = [...this.filters, { column: col, op: '=', value: '' }];
    },
    removeFilter(i) {
      this.filters = this.filters.filter((_, idx) => idx !== i);
    },

    _filterToConfig(f) {
      const out = { column: f.column, op: f.op };
      if (this._isNullOp(f.op)) return out;
      if (this._isListOp(f.op)) {
        out.value = String(f.value).split(',').map(s => s.trim()).filter(Boolean);
      } else {
        // numeric if it looks numeric, else string
        const n = Number(f.value);
        out.value = (f.value !== '' && !Number.isNaN(n)) ? n : f.value;
      }
      return out;
    },

    // Rebuild column overrides from a saved config's output_columns. A present
    // list means the user narrowed/renamed: listed cols are included (with any
    // alias), and — once the full column set is known — anything NOT listed is
    // excluded. We can't know the full set until nodes exist, so mark listed
    // cols here and let _reconcileExcluded (called after nodes build) flip the
    // unlisted ones off.
    _overridesFromConfig(cfg) {
      const oc = (cfg && cfg.output_columns) || null;
      if (!oc || !oc.length) return {};
      const ov = {};
      this._explicitOutputCols = new Set();
      for (const entry of oc) {
        if (typeof entry === 'string') { ov[entry] = { included: true }; this._explicitOutputCols.add(entry); }
        else if (entry && entry.col) { ov[entry.col] = { included: true, alias: entry.as || '' }; this._explicitOutputCols.add(entry.col); }
      }
      return ov;
    },

    // After nodes are built, any column NOT in a saved output_columns list is
    // excluded (the user had narrowed the output). Only runs when a saved list
    // existed (_explicitOutputCols set during rehydrate).
    _reconcileExcluded() {
      if (!this._explicitOutputCols || !this._explicitOutputCols.size) return;
      const ov = { ...this.columnOverrides };
      for (const c of this.outputColumns) {
        if (!this._explicitOutputCols.has(c)) ov[c] = { ...(ov[c] || {}), included: false };
      }
      this.columnOverrides = ov;
      this._explicitOutputCols = null;
    },

    // --- output-columns picker ---
    isIncluded(col) {
      const o = this.columnOverrides[col];
      return !o || o.included !== false;  // default included
    },
    aliasFor(col) {
      const o = this.columnOverrides[col];
      return (o && o.alias) || '';
    },
    toggleColumn(col) {
      const o = { ...(this.columnOverrides[col] || {}) };
      o.included = !this.isIncluded(col);
      this.columnOverrides = { ...this.columnOverrides, [col]: o };
    },
    setAlias(col, val) {
      const o = { ...(this.columnOverrides[col] || {}) };
      o.alias = (val || '').trim();
      this.columnOverrides = { ...this.columnOverrides, [col]: o };
    },

    // Build the output_columns list from the column overrides, or null when the
    // user hasn't customized (all columns included, none renamed) → SELECT *.
    _emitOutputColumns() {
      const all = this.outputColumns;
      if (!all.length) return null;
      const ov = this.columnOverrides;
      const touched = all.some(c => ov[c] && (ov[c].included === false || ov[c].alias));
      if (!touched) return null;
      const out = [];
      for (const c of all) {
        const o = ov[c] || {};
        if (o.included === false) continue;
        out.push(o.alias ? { col: c, as: o.alias } : c);
      }
      return out;
    },

    // Joined nodes in dependency order, breadth-first from the base, so every
    // node appears after the node it joins to. Nodes unreachable from the base
    // (no incoming edge, or only reachable through a cycle) are left out — they
    // surface via unjoinedNodes() instead.
    _orderedJoinNodes(base) {
      const ordered = [];
      const placed = new Set([base.id]);
      let frontier = [base.id];
      while (frontier.length) {
        const nextFrontier = [];
        for (const srcId of frontier) {
          for (const e of this.edges) {
            if (e.source !== srcId || placed.has(e.target)) continue;
            const node = this.nodes.find(n => n.id === e.target);
            if (!node) continue;
            placed.add(node.id);
            ordered.push(node);
            nextFrontier.push(node.id);
          }
        }
        frontier = nextFrontier;
      }
      return ordered;
    },

    // Nodes on the canvas that contribute nothing to studio_config: no incoming
    // join, or joined only in a way unreachable from the base (e.g. a cycle
    // among themselves). Defined as the complement of what buildStudioConfig
    // actually emits, so the warning can never disagree with the compiled
    // output. Surfaced as a canvas warning rather than dropped without a word.
    unjoinedNodes() {
      const base = this.nodes.find(n => n.data.role === 'population');
      if (!base) return [];
      const emitted = new Set(this._orderedJoinNodes(base).map(n => n.id));
      return this.nodes.filter(n => n.id !== base.id && !emitted.has(n.id));
    },

    emitConfig() {
      const cfg = this.buildStudioConfig();
      const key = JSON.stringify(cfg);
      if (key === this.lastEmittedKey) return;
      this.lastEmittedKey = key;
      this.lastEmitted = cfg;
      this.$emit('update:modelValue', cfg);
    },

    // Is this incoming modelValue the parent bouncing back the very object we
    // emitted? Identity-based on purpose: anything else — including a config
    // that serializes identically — is an authoritative load from the parent
    // (selectWorkflow after a lifecycle action) and MUST rehydrate.
    isOwnEcho(cfg) {
      return !!cfg && toRaw(cfg) === toRaw(this.lastEmitted);
    },

    // Rebuild the visual graph from a saved studio_config so EDITING a workflow
    // shows its real nodes + joins (inverse of buildStudioConfig). Step 0
    // (role 'population') is the base; each later step becomes a joined node.
    //
    // The edge SOURCE is recovered from the left-hand alias of join_on_sql,
    // mapped back through the compiler's positional aliasing (step 0 -> 'a',
    // step 1 -> 'b', …). Previously every edge was hardcoded to the base, so a
    // saved chain (A->B->C) silently reloaded as a star and the next save
    // persisted the star — changing the join semantics without the user
    // touching anything.
    rehydrate(cfg) {
      this.hydrating = true;
      this.nodes = [];
      this.edges = [];
      this.editingEdge = null;
      this.seq = 0;
      // Restore column select/rename overrides from a saved output_columns list.
      this.columnOverrides = this._overridesFromConfig(cfg);
      // Restore output filters from the saved config (value list -> comma string
      // for the single value input).
      this.filters = ((cfg && cfg.output_filters) || []).map(f => ({
        column: f.column || '',
        op: f.op || '=',
        value: Array.isArray(f.value) ? f.value.join(', ') : (f.value == null ? '' : String(f.value)),
      }));
      this.calculated = JSON.parse(JSON.stringify((cfg && cfg.calculated_columns) || []));
      const steps = (cfg && cfg.steps) || [];
      if (!steps.length) { this.$nextTick(() => { this.hydrating = false; }); return; }

      // Base node.
      const baseStep = steps[0];
      const baseId = `n${this.seq++}`;
      this.nodes.push(this.buildNode(baseId, baseStep, 'population', { x: 60, y: 70 }));

      // Step index -> node id, so a join's alias can resolve to its real node.
      const idByStepIndex = { 0: baseId };

      const gap = this.edgeGap(steps.slice(1).map((step, k) => {
        const { key, keys } = this._parseJoin(step.join_on_sql, k + 1);
        return { data: { step_op: step.step_op || 'join', join_type: step.join_type || 'JOIN', join_key: key || '', join_keys: keys } };
      }));
      // Joined nodes first, so a forward reference still resolves.
      for (let i = 1; i < steps.length; i++) {
        const nodeId = `n${this.seq++}`;
        idByStepIndex[i] = nodeId;
        this.nodes.push(this.buildNode(nodeId, steps[i], 'denominator', { x: 60 + i * (this.NODE_W + gap), y: 70 }));
      }

      // Then the edges, each anchored to the step its join_on_sql names.
      for (let i = 1; i < steps.length; i++) {
        const step = steps[i];
        const nodeId = idByStepIndex[i];
        const op = step.step_op || 'join';
        if (op === 'union' || op === 'union_all') {
          // Union steps have no join_on_sql — anchor them to the base.
          this.edges.push({
            id: `e-${baseId}-${nodeId}`,
            source: baseId, target: nodeId, updatable: true,
            label: op === 'union_all' ? 'union all' : 'union',
            data: { step_op: op, join_type: 'JOIN', join_key: '' },
          });
          continue;
        }
        const { key, keys, sourceIndex } = this._parseJoin(step.join_on_sql, i);
        const sourceId = idByStepIndex[sourceIndex] || baseId;
        this.edges.push({
          id: `e-${sourceId}-${nodeId}`,
          source: sourceId, target: nodeId, updatable: true,
          label: keys.length ? `on ${keys.join(' + ')}` : 'join',
          data: {
            step_op: 'join', join_type: step.join_type || 'JOIN', join_key: key || '',
            join_keys: keys.length > 1 ? keys : null,
          },
        });
      }
      // Now that every node's columns are known, flip off any column the saved
      // output_columns list omitted (must run after nodes build).
      this._reconcileExcluded();
      // Release the guard after the graph settles so the watchers don't re-emit
      // the just-loaded config back to the parent.
      this.$nextTick(() => { this.hydrating = false; });
    },

    // Build one source node for a studio_config step, resolving columns from the
    // loaded DA library (config only stores workflow_id).
    buildNode(nodeId, step, role, pos) {
      const full = this.availableDas.find(d => d.workflow_id === step.workflow_id);
      return {
        id: nodeId,
        type: 'source',
        position: pos,
        data: {
          id: nodeId, workflow_id: step.workflow_id,
          name: (full && full.name) || `Workflow ${step.workflow_id}`,
          role,
          columns: (full && full.output_columns) || [],
        },
      };
    },

    // Pull the join key AND the source step out of a "<src>.COL = <self>.COL"
    // clause (buildStudioConfig's format). The alias is positional in the
    // compiler, so 'a' -> step 0, 'b' -> step 1, … Matching the alias generally
    // (not a literal 'a.') is what lets a chained join round-trip; the old
    // hardcoded /a\.(...)=b\.(...)/ returned null for every non-base join, which
    // dropped its key as well as its source.
    //
    // selfIndex is the step being parsed: whichever side of the equality is NOT
    // this step is the source. Falls back to the base (step 0) for anything
    // non-trivial — a hand-written or multi-condition clause the canvas can't
    // represent — so an unparseable config still loads as a usable graph.
    _parseJoin(sql, selfIndex) {
      const fallback = { key: null, keys: [], sourceIndex: 0 };
      if (!sql) return fallback;
      const idx = alias => alias.charCodeAt(0) - 97;
      const keys = [];
      let sourceIndex = null;
      for (const part of String(sql).split(/\s+AND\s+/i)) {
        const m = part.trim().match(/^([A-Za-z])\.([A-Za-z0-9_]+)\s*=\s*([A-Za-z])\.([A-Za-z0-9_]+)$/);
        if (!m) continue;
        const [, leftAlias, leftCol, rightAlias, rightCol] = m;
        const leftIdx = idx(leftAlias);
        const rightIdx = idx(rightAlias);
        const other = rightIdx === selfIndex ? leftIdx : rightIdx;
        if (sourceIndex === null) sourceIndex = other;
        keys.push(rightIdx === selfIndex ? leftCol : rightCol);
      }
      if (!keys.length) return fallback;
      return { key: keys[0], keys, sourceIndex };
    },
  },
};
</script>

<style scoped>
/* Kairon brand tokens (theme.css vars in-app; hardcoded fallbacks for the
   standalone preview). tangerine = primary, blue/green = health triad, teal =
   interaction accent. Nodes are color-coded by role so the graph reads at a
   glance. */
.wf-canvas-wrap {
  display: flex; height: 440px; border-radius: 12px; overflow: hidden; position: relative;
  border: 1px solid var(--color-primary-100, #FEE3D9);
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
}
.wf-canvas-rail {
  width: 190px; padding: 14px 13px; overflow-y: auto;
  border-right: 1px solid var(--color-primary-100, #FEE3D9);
  background: linear-gradient(180deg, var(--color-primary-50, #FFF7F4), #ffffff);
  font-family: inherit; -webkit-font-smoothing: antialiased;
}
.wf-rail-title { font-size: 13px; font-weight: 600; color: #452A20; margin-bottom: 10px; }
.wf-rail-select {
  width: 100%; box-sizing: border-box; font-size: 12px; font-weight: 500; color: #452A20;
  padding: 7px 9px; border-radius: 8px; border: 1px solid var(--color-primary-300, #FBAA8C);
  background: #fff; cursor: pointer; transition: border-color .12s ease, box-shadow .12s ease;
}
.wf-rail-select:hover:not(:disabled) { border-color: var(--color-primary-500, #F76734); }
.wf-rail-select:focus { outline: none; border-color: var(--color-primary-500, #F76734); box-shadow: 0 0 0 3px rgba(247,103,52,.15); }
.wf-rail-select:disabled { opacity: .6; cursor: default; }
.wf-rail-sub { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #b08b7a; margin: 12px 0 7px; }
.wf-rail-card {
  display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 500;
  padding: 8px 10px; margin-bottom: 7px; background: #fff; border-radius: 8px;
  border: 1px solid var(--color-primary-200, #FDC6B3); color: #452A20;
  cursor: grab; transition: all .12s ease;
}
.wf-rail-card:hover {
  border-color: var(--color-primary-500, #F76734);
  box-shadow: 0 2px 6px rgba(247,103,52,.18); transform: translateY(-1px);
}
.wf-rail-card-icon { color: var(--color-primary-500, #F76734); font-size: 11px; flex-shrink: 0; }
.wf-rail-card.is-unavailable { opacity: .5; cursor: not-allowed; }
.wf-rail-card-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wf-rail-empty { font-size: 11px; color: #b08b7a; line-height: 1.4; }
.wf-canvas { flex: 1; position: relative; background:
  radial-gradient(circle at 1px 1px, var(--color-primary-100, #FEE3D9) 1px, transparent 0) 0 0/20px 20px,
  #ffffff; }
.wf-canvas-hint { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 16px; pointer-events: none; }
.wf-guide { width: 100%; max-width: 560px; background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 8px; padding: 20px; }
.wf-guide-title { margin: 0 0 16px; font-size: 14px; font-weight: 600; color: #1E1F21; }
.wf-guide-steps { list-style: none; margin: 0; padding: 0; display: flex; gap: 16px; }
.wf-guide-steps li { flex: 1; display: flex; flex-direction: column; gap: 8px; position: relative; font-size: 12px; line-height: 1.45; color: #626264; }
.wf-guide-steps strong { display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600; color: #353537; }
.wf-guide-num { position: absolute; top: -4px; left: 28px; width: 16px; height: 16px; border-radius: 9999px; background: #F76734; color: #FFFFFF; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.wf-guide-icon { width: 36px; height: 36px; border-radius: 8px; background: #FFF7F4; color: #9F4D30; display: flex; align-items: center; justify-content: center; font-size: 14px; }
.wf-guide-foot { margin: 16px 0 0; padding-top: 16px; border-top: 1px solid #E8E9E9; font-size: 12px; color: #626264; }

/* Source node: white body with a thicker colored border ALL AROUND (by role)
   + a matching tinted header. Crisp, Kairon-colored. */
.wf-node {
  position: relative; width: 168px; border-radius: 11px; background: #fff;
  border: 2px solid #e5e7eb; box-shadow: 0 4px 12px rgba(16,24,40,.10);
  padding: 0; overflow: hidden;
}
.wf-node-head {
  display: flex; align-items: center; gap: 7px; padding: 8px 11px;
  font-size: 12px; font-weight: 700; letter-spacing: .01em;
}
.wf-node-name {
  font-size: 12px; font-weight: 600; color: #1f2937; word-break: break-all;
  padding: 7px 11px 5px; background: #fff;
}
.wf-node-cols {
  list-style: none; margin: 0; padding: 0 11px 8px; background: #fff;
  display: flex; flex-direction: column; gap: 2px;
}
.wf-node-col { cursor: grab; }
.wf-node-col:hover { color: var(--color-accent, #0E8C9E); }
.wf-node-cols li {
  font: 10px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; color: #6b7280;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.wf-node-x {
  position: absolute; top: 6px; right: 7px; width: 18px; height: 18px;
  display: flex; align-items: center; justify-content: center;
  border: none; background: transparent; color: #ffffffcc; font-size: 10px;
  border-radius: 50%; cursor: pointer; transition: background .12s ease, color .12s ease;
}
.wf-node-x:hover { background: rgba(255,255,255,.35); color: #fff; }

/* population = tangerine (the anchor / brand) */
.wf-node--population { border-color: var(--color-primary-500, #F76734); }
.wf-node--population .wf-node-head { color: #fff; background: var(--color-primary-500, #F76734); }
.wf-node--population .wf-node-name { border-top: 1px solid var(--color-primary-100, #FEE3D9); }
/* denominator = blue */
.wf-node--denominator { border-color: var(--color-blue-dark, #4573D2); }
.wf-node--denominator .wf-node-head { color: #fff; background: var(--color-blue-dark, #4573D2); }
.wf-node--denominator .wf-node-name { border-top: 1px solid #DCE6FA; }
/* intervention = green */
.wf-node--intervention { border-color: var(--color-green-dark, #389A6C); }
.wf-node--intervention .wf-node-head { color: #fff; background: var(--color-green-dark, #389A6C); }
.wf-node--intervention .wf-node-name { border-top: 1px solid var(--color-secondary-200, #E3F3E0); }

/* Join editor popover — self-contained styling (no reliance on app utilities),
   sleek + compact. */
/* Teleported to <body> and fixed at the clicked edge, clamped to the
   viewport, so a short canvas never clips it. */
.wf-join-editor {
  position: fixed; width: 232px; z-index: 60; max-height: calc(100vh - 24px); overflow-y: auto;
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  box-shadow: 0 10px 28px rgba(16,24,40,.16); padding: 14px 15px;
  font-family: inherit; -webkit-font-smoothing: antialiased;
  animation: wf-pop .12s ease-out;
}
@keyframes wf-pop {
  from { opacity: 0; transform: translateY(-4px) scale(.98); }
  to   { opacity: 1; transform: none; }
}
.wf-je-title {
  display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600;
  color: #111827; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #f1f2f4;
}
.wf-je-title-icon {
  color: var(--color-accent, #0E8C9E); font-size: 11px;
  background: var(--color-accent-50, #E2F1F3); padding: 4px; border-radius: 6px;
}
.wf-je-label {
  display: block; font-size: 11px; font-weight: 500; color: #6b7280;
  margin-bottom: 4px; letter-spacing: .01em;
}
.wf-je-input {
  width: 100%; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 8px;
  padding: 7px 9px; font-size: 13px; color: #1f2937; background: #fff; margin-bottom: 12px;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.wf-je-input:focus { outline: none; border-color: var(--color-accent, #0E8C9E); box-shadow: 0 0 0 3px rgba(14,140,158,.15); }
.wf-je-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.wf-je-note { margin: -8px 0 12px; font-size: 10px; color: #9ca3af; line-height: 1.3; }
.wf-je-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 2px; }
.wf-je-btn {
  font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 8px; cursor: pointer;
  border: 1px solid transparent; transition: all .12s ease;
}
.wf-je-btn--remove { background: transparent; color: var(--color-red-dark, #E05252); }
.wf-je-btn--remove:hover { background: #fdecec; }
.wf-je-btn--done { background: var(--color-accent, #0E8C9E); color: #fff; }
.wf-je-btn--done:hover { background: #0b7686; }

/* Hover tooltip: any .wf-tip with a data-tip shows its label above, on hover.
   Used by the join button and our own zoom controls (fully in our DOM, so no
   clipping — unlike vue-flow's built-in Controls). */
.wf-tip { position: relative; }
.wf-tip::after {
  content: attr(data-tip);
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: #1f2937; color: #fff; font-size: 11px; font-weight: 500; line-height: 1.2;
  padding: 5px 8px; border-radius: 6px; white-space: nowrap; max-width: 220px;
  opacity: 0; pointer-events: none; transition: opacity .12s ease; z-index: 30;
}
.wf-tip:hover::after { opacity: 1; }

/* Studio-config toggle (bottom-right, above the zoom stack) */
.wf-cfg-btn {
  position: relative;
  width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #e5e7eb; color: #4b5563; font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1); cursor: pointer; transition: all .12s ease;
}
.wf-cfg-btn:hover { border-color: var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E); }
.wf-cfg-btn--on { background: var(--color-accent, #0E8C9E); border-color: var(--color-accent, #0E8C9E); color: #fff; }

/* Output-columns picker toggle (above the config button, at 156px) */
.wf-cols-btn {
  position: relative;
  width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #e5e7eb; color: #4b5563; font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1); cursor: pointer; transition: all .12s ease;
}
.wf-cols-btn:hover:not(:disabled) { border-color: var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E); }
.wf-cols-btn--on { background: var(--color-accent, #0E8C9E); border-color: var(--color-accent, #0E8C9E); color: #fff; }
.wf-cols-btn:disabled { opacity: .5; cursor: default; }

/* Calculated-columns toggle (above the filters button, at 228px) */
.wf-calc-btn {
  position: relative;
  width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #e5e7eb; color: #4b5563; font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1); cursor: pointer; transition: all .12s ease;
}
.wf-calc-btn:hover:not(:disabled) { border-color: var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E); }
.wf-calc-btn--on { background: var(--color-accent, #0E8C9E); border-color: var(--color-accent, #0E8C9E); color: #fff; }
.wf-calc-btn:disabled { opacity: .5; cursor: default; }
.wf-calc { width: 420px; }
.wf-cols-body, .wf-filt-body, .wf-cfg-body { min-height: 0; }
.wf-cols-body::-webkit-scrollbar, .wf-filt-body::-webkit-scrollbar, .wf-cfg-body::-webkit-scrollbar, .wf-join-editor::-webkit-scrollbar { width: 10px; height: 10px; }
.wf-cols-body::-webkit-scrollbar-track, .wf-filt-body::-webkit-scrollbar-track, .wf-join-editor::-webkit-scrollbar-track { background: #F4F4F4; border-radius: 9999px; }
.wf-cols-body::-webkit-scrollbar-thumb, .wf-filt-body::-webkit-scrollbar-thumb, .wf-join-editor::-webkit-scrollbar-thumb { background: #BBBCBC; border-radius: 9999px; border: 2px solid #F4F4F4; }
.wf-cols-body::-webkit-scrollbar-thumb:hover, .wf-filt-body::-webkit-scrollbar-thumb:hover, .wf-join-editor::-webkit-scrollbar-thumb:hover { background: #9D9D9F; }
.wf-cfg-body::-webkit-scrollbar-track { background: #1e293b; border-radius: 9999px; }
.wf-cfg-body::-webkit-scrollbar-thumb { background: #475569; border-radius: 9999px; border: 2px solid #1e293b; }
.wf-cfg-body::-webkit-scrollbar-thumb:hover { background: #64748b; }
.wf-cfg-body::-webkit-scrollbar-corner, .wf-cols-body::-webkit-scrollbar-corner, .wf-filt-body::-webkit-scrollbar-corner { background: transparent; }
@supports not selector(::-webkit-scrollbar) {
  .wf-cols-body, .wf-filt-body, .wf-join-editor { scrollbar-width: thin; scrollbar-color: #BBBCBC #F4F4F4; }
  .wf-cfg-body { scrollbar-width: thin; scrollbar-color: #475569 #1e293b; }
}
.wf-calc-row { display: flex; align-items: center; gap: 8px; padding: 4px 8px; }
.wf-calc-name { border: none; background: transparent; padding: 0; cursor: pointer; font: 11.5px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; color: #1f2937; }
.wf-calc-name:hover { color: var(--color-accent, #0E8C9E); text-decoration: underline; }
.wf-calc-fn { margin-left: auto; font-size: 11px; color: #9ca3af; }

/* Output-filters toggle (above the columns button, at 192px) */
.wf-filt-btn {
  position: relative;
  width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #e5e7eb; color: #4b5563; font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1); cursor: pointer; transition: all .12s ease;
}
.wf-filt-btn:hover:not(:disabled) { border-color: var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E); }
.wf-filt-btn--on { background: var(--color-accent, #0E8C9E); border-color: var(--color-accent, #0E8C9E); color: #fff; }
.wf-filt-btn:disabled { opacity: .5; cursor: default; }
.wf-filt-badge {
  position: absolute; top: -5px; right: -5px; min-width: 15px; height: 15px; padding: 0 3px;
  border-radius: 8px; background: var(--color-primary-500, #F76734); color: #fff;
  font-size: 9px; font-weight: 700; line-height: 15px; text-align: center;
}

/* Output columns panel (bottom-right; mutually exclusive with the filters panel) */
.wf-cols {
  position: absolute; right: 12px; top: 52px; z-index: 16; width: 300px; max-height: calc(100% - 64px);
  background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden;
  box-shadow: 0 10px 28px rgba(16,24,40,.18); display: flex; flex-direction: column;
}
.wf-cols-head {
  display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-bottom: 1px solid #f1f2f4;
  font-size: 12px; font-weight: 600; color: #374151;
}
.wf-cols-head-icon { color: var(--color-accent, #0E8C9E); font-size: 11px; }
.wf-cols-meta { font-weight: 500; color: #9ca3af; font-size: 11px; }
.wf-cols-x { margin-left: auto; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 12px; }
.wf-cols-x:hover { color: #4b5563; }
.wf-cols-body { overflow-y: auto; padding: 6px 4px; }
.wf-cols-empty { padding: 16px 12px; font-size: 12px; color: #9ca3af; text-align: center; }
.wf-cols-row { display: flex; align-items: center; gap: 8px; padding: 4px 8px; }
.wf-cols-check { display: flex; align-items: center; gap: 7px; flex: 1; min-width: 0; cursor: pointer; }
.wf-cols-name {
  font: 11px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; color: #374151;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.wf-cols-name--off { color: #c0c4cc; text-decoration: line-through; }
.wf-cols-rename {
  width: 108px; box-sizing: border-box; border: 1px solid #e5e7eb; border-radius: 6px;
  padding: 3px 7px; font-size: 11px; color: #1f2937; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.wf-cols-rename:focus { outline: none; border-color: var(--color-accent, #0E8C9E); }
.wf-cols-rename:disabled { background: #f9fafb; opacity: .6; }

/* Output filters panel (bottom-right; mutually exclusive with the columns panel) */
.wf-filt {
  position: absolute; right: 12px; top: 52px; z-index: 16; width: 340px; max-height: calc(100% - 64px);
  background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden;
  box-shadow: 0 10px 28px rgba(16,24,40,.18); display: flex; flex-direction: column;
}
.wf-filt-head {
  display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-bottom: 1px solid #f1f2f4;
  font-size: 12px; font-weight: 600; color: #374151;
}
.wf-filt-head-icon { color: var(--color-accent, #0E8C9E); font-size: 11px; }
.wf-filt-x { margin-left: auto; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 12px; }
.wf-filt-x:hover { color: #4b5563; }
.wf-filt-body { overflow-y: auto; padding: 8px; }
.wf-filt-empty { padding: 10px 4px; font-size: 12px; color: #9ca3af; text-align: center; }
.wf-filt-row { display: flex; align-items: center; gap: 5px; margin-bottom: 6px; }
.wf-filt-col, .wf-filt-op, .wf-filt-val {
  border: 1px solid #d1d5db; border-radius: 6px; padding: 4px 6px; font-size: 11px; color: #1f2937; background: #fff;
}
.wf-filt-col { flex: 1.4; min-width: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.wf-filt-op { flex: 1; min-width: 0; }
.wf-filt-val { flex: 1.2; min-width: 0; }
.wf-filt-col:focus, .wf-filt-op:focus, .wf-filt-val:focus { outline: none; border-color: var(--color-accent, #0E8C9E); }
.wf-filt-del { border: none; background: transparent; color: #b0b4bb; cursor: pointer; font-size: 11px; padding: 2px 4px; }
.wf-filt-del:hover { color: var(--color-red-dark, #E05252); }
.wf-filt-add {
  display: inline-flex; align-items: center; gap: 6px; margin-top: 2px; padding: 5px 10px;
  border: 1px dashed var(--color-primary-300, #FBAA8C); border-radius: 7px; background: #fff;
  color: var(--color-primary-600, #CC5F38); font-size: 12px; font-weight: 600; cursor: pointer;
}
.wf-filt-add:hover { border-color: var(--color-primary-500, #F76734); background: var(--color-primary-50, #FFF7F4); }

/* Live studio_config panel (bottom-right) — crisp dark code card. */
.wf-cfg {
  position: absolute; right: 12px; top: 52px; z-index: 16; width: 320px; max-height: calc(100% - 64px);
  background: #0f172a; border: 1px solid #1e293b; border-radius: 10px; overflow: hidden;
  box-shadow: 0 10px 28px rgba(16,24,40,.28); display: flex; flex-direction: column;
}
.wf-cfg-head {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px;
  border-bottom: 1px solid #1e293b; font-size: 12px; font-weight: 600; color: #e2e8f0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.wf-cfg-head-icon { color: var(--color-accent, #0E8C9E); font-size: 11px; }
.wf-cfg-meta { font-weight: 500; color: #64748b; font-size: 11px; }
.wf-cfg-x { margin-left: auto; border: none; background: transparent; color: #64748b; cursor: pointer; font-size: 11px; }
.wf-cfg-x:hover { color: #e2e8f0; }
.wf-cfg-body {
  margin: 0; padding: 10px 12px; overflow: auto; flex: 1;
  font: 11px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; color: #93c5fd;
  white-space: pre; tab-size: 2;
}

/* Own zoom controls (bottom-right) */
.wf-tools { position: absolute; right: 12px; top: 12px; z-index: 15; display: flex; align-items: center; gap: 4px; }
.wf-tools-sep { width: 1px; height: 20px; margin: 0 4px; background: #e5e7eb; }
.wf-tools .wf-tip::after { bottom: auto; top: calc(100% + 8px); left: auto; right: 0; transform: none; }
.wf-zoom-btn {
  width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #e5e7eb; color: #4b5563; font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1); cursor: pointer; transition: all .12s ease;
}
.wf-zoom-btn:hover { border-color: var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E); }
.wf-zoom-glyph { font-size: 16px; line-height: 1; font-weight: 600; }

/* Preview button (bottom-left, next to Join) + results panel */
.wf-preview-btn {
  position: absolute; left: 60px; bottom: 12px; z-index: 15;
  display: flex; align-items: center; gap: 6px; height: 38px; padding: 0 14px;
  border-radius: 9px; font-size: 12px; font-weight: 600; cursor: pointer;
  background: #fff; border: 1px solid var(--color-primary-300, #FBAA8C); color: var(--color-primary-600, #CC5F38);
  box-shadow: 0 2px 8px rgba(0,0,0,.10); transition: all .12s ease;
}
.wf-preview-btn:hover:not(:disabled) { border-color: var(--color-primary-500, #F76734); box-shadow: 0 3px 10px rgba(247,103,52,.22); }
.wf-preview-btn:disabled { opacity: .5; cursor: default; }
.wf-preview {
  position: absolute; left: 12px; right: 12px; bottom: 58px; z-index: 16; max-height: 190px;
  background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden;
  box-shadow: 0 8px 24px rgba(16,24,40,.16); display: flex; flex-direction: column;
}
.wf-preview-head {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid #f1f2f4;
  font-size: 12px; font-weight: 600; color: #374151;
}
.wf-preview-count { font-weight: 500; color: #9ca3af; }
.wf-preview-x { margin-left: auto; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 12px; }
.wf-preview-x:hover { color: #4b5563; }
.wf-preview-error { padding: 12px; font-size: 12px; color: var(--color-red-dark, #E05252); }
.wf-preview-scroll { overflow: auto; }
.wf-preview-table { border-collapse: collapse; width: 100%; font-size: 11px; }
.wf-preview-table th {
  position: sticky; top: 0; background: #f9fafb; text-align: left; padding: 6px 12px;
  font-weight: 600; color: #6b7280; border-bottom: 1px solid #eceef1;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap;
}
.wf-preview-table td {
  padding: 5px 12px; border-bottom: 1px solid #f3f4f6; color: #374151;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap;
}

/* Auto-arrange button (bottom-left) — the slot the join toggle used to hold. */
.wf-arrange-btn {
  position: absolute; left: 12px; bottom: 12px; z-index: 15;
  width: 38px; height: 38px; border-radius: 9px; display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid var(--color-accent, #0E8C9E); color: var(--color-accent, #0E8C9E);
  box-shadow: 0 2px 8px rgba(0,0,0,.12); cursor: pointer; transition: all .12s ease;
}
.wf-arrange-btn:hover:not(:disabled) {
  box-shadow: 0 3px 10px rgba(14,140,158,.28); transform: translateY(-1px);
}
.wf-arrange-btn:disabled { opacity: .45; cursor: default; }

/* The canvas takes focus so it can receive key events, but must not show the
   browser's focus ring — it is a surface, not a control. */
.wf-canvas:focus, .wf-canvas:focus-visible { outline: none; }

/* Unjoined-node warning (top-center, click-through) */
.wf-warn {
  position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 17;
  display: flex; align-items: flex-start; gap: 7px; max-width: calc(100% - 24px);
  padding: 7px 11px; border-radius: 8px; font-size: 11px; line-height: 1.35;
  background: #FEF6E7; border: 1px solid #F5D48A; color: #8A5A00;
  box-shadow: 0 2px 8px rgba(16,24,40,.10); pointer-events: none;
}

/* Minimap: bottom-right, deliberately understated so it orients without
   competing with the canvas itself. */
.wf-minimap {
  position: absolute !important; right: 12px; bottom: 12px;
  width: 150px; height: 104px; border-radius: 9px; overflow: hidden;
  background: rgba(255,255,255,.92); border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(16,24,40,.10);
}

/* CONNECT HANDLES — the heart of the rework.
   Hidden at rest so a card reads as a clean object; they fade in on hover of
   the card, and stay visible while a connection is being dragged so you can
   see where a drop will land. No mode, no toggle: drag the body to move, drag
   a handle to join. */
.wf-handle {
  width: 12px; height: 12px; border: 2px solid #fff; border-radius: 50%;
  background: var(--color-accent, #0E8C9E);
  opacity: 0; transform: scale(.6);
  transition: opacity .12s ease, transform .12s ease, box-shadow .12s ease;
  cursor: crosshair;
}
.wf-node:hover .wf-handle,
.wf-node--selected .wf-handle { opacity: 1; transform: scale(1); }
.wf-handle:hover {
  transform: scale(1.35);
  box-shadow: 0 0 0 4px var(--color-accent-50, #E2F1F3);
}
/* While dragging a connection anywhere on the canvas, reveal every handle so
   valid drop targets are obvious rather than discovered by trial. */
.wf-node--connecting .wf-handle { opacity: 1; transform: scale(1); }

/* Full-card drop zone: inert until a connection is in flight, then it covers
   the card so a drop anywhere lands. Sits beneath the card's content in
   z-order but above the background, and never takes pointer events at rest. */
/* !important throughout: vue-flow's own .vue-flow__handle rules set an
   explicit width/height/position and win on specificity, which silently
   collapsed this to a 12x12 dot in the card's top-left corner — the drop
   "anywhere on the card" then only worked in that one square. */
.wf-handle-dropzone {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  transform: none !important;
  border: none !important;
  border-radius: 11px !important;
  background: transparent !important;
  opacity: 0 !important;
  pointer-events: none;
  /* Must out-rank the card's own content, or elementFromPoint returns the text
     under the cursor and vue-flow never sees a handle to drop onto. */
  z-index: 20;
}
.wf-node--connecting .wf-handle-dropzone { pointer-events: all; }
/* Tint the card under the cursor during a connection so the drop target is
   unmistakable rather than inferred. */
.wf-node--connecting:hover {
  box-shadow: 0 0 0 2px var(--color-accent, #0E8C9E), 0 4px 16px rgba(14,140,158,.22);
}

/* First-run affordance on an unconnected card: a soft pulse on the out-handle
   that stops mattering as soon as the card is joined. */
.wf-handle-pulse {
  position: absolute; right: -6px; top: 50%; margin-top: -6px;
  width: 12px; height: 12px; border-radius: 50%; pointer-events: none;
  background: var(--color-accent, #0E8C9E); opacity: .35;
  animation: wf-pulse 2s ease-out infinite;
}
@keyframes wf-pulse {
  0%   { transform: scale(.8); opacity: .35; }
  70%  { transform: scale(2.1); opacity: 0; }
  100% { transform: scale(2.1); opacity: 0; }
}
.wf-node:hover .wf-handle-pulse { display: none; }

/* Selection: a clear ring, since Delete acts on the selection. */
.wf-node--selected {
  box-shadow: 0 0 0 2px var(--color-accent, #0E8C9E), 0 4px 14px rgba(16,24,40,.14);
}
:deep(.vue-flow__node) { cursor: grab; }
:deep(.vue-flow__node:active) { cursor: grabbing; }

/* teal join edges (interaction accent) + the native connection line while dragging */
:deep(.vue-flow__edge-path) { stroke: var(--color-accent, #0E8C9E); stroke-width: 2; }
:deep(.vue-flow__edge-text) { fill: #21426C; font: 600 11px ui-monospace, SFMono-Regular, Menlo, monospace; }
:deep(.vue-flow__edge-textbg) { fill: #FFFFFF; stroke: #C1D6F0; stroke-width: 1px; }
:deep(.vue-flow__connectionline .vue-flow__connection-path),
:deep(.vue-flow__connection-path) {
  stroke: var(--color-accent, #0E8C9E); stroke-width: 2; stroke-dasharray: 5 4;
}
:deep(.vue-flow__handle) { background: var(--color-primary-500, #F76734); border: 2px solid #fff; width: 9px; height: 9px; }
/* valid drop target highlight during a native connection */
:deep(.vue-flow__node.connecting .wf-node),
:deep(.vue-flow__handle-connecting) { box-shadow: 0 0 0 3px var(--color-accent, #0E8C9E); }
</style>
